/**
 * Test types and a register of records, for the sample estate.
 *
 * The sample declared no test types and held no records, so Testing &
 * Records opened empty: nothing to search, and no test could be recorded
 * either, because a record is recorded against a type and there were none.
 * A screen built for storing and finding evidence showed neither.
 *
 * DEMONSTRATION DATA. Every record carries `demo: true`, every tile that
 * counts them says so, and the register can filter them out in one click.
 * Nothing here is Madinah's evidence.
 *
 * Two things are deliberate and should survive editing.
 *
 * **Every threshold and interval is `proposed`.** These are the values the
 * standards usually carry, not values this airport has agreed. A test that
 * grades an asset against a limit nobody signed is an opinion wearing the
 * clothes of a measurement, and the screens say `proposed` beside each one
 * for exactly that reason.
 *
 * **The register is not tidy.** One record names no service provider, two
 * providers differ only by a suffix, and one test fails. A register where
 * every row is complete and every verdict is a pass demonstrates nothing —
 * the point of being able to search by provider is to find the records that
 * name nobody, and the point of a verdict filter is that some tests fail.
 */
import type { NewTestInput, TestRecord, TestType, TestingSummary } from "../domain/testing";

const DAY = 86_400_000;
const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Anchored to the clock: a register whose newest record is a year old reads as abandoned. */
const ago = (now: Date, days: number) => iso(new Date(now.getTime() - days * DAY));
const ahead = (now: Date, days: number) => iso(new Date(now.getTime() + days * DAY));

/*
 * build 6B: thresholds confirmed or changed in this session. Every reader of
 * the sample's types — the list, the summary, a new record's verdict — goes
 * through mockTestTypes(), so a change here moves all of them, and records
 * already judged keep the copy they were judged against, as on the platform.
 */
const CHANGED = new Map<string, Partial<TestType>>();

export function setMockThresholds(key: string, change: Partial<TestType>): TestType {
  const base = baseTestTypes().find((t) => t.key === key);
  if (!base) throw new Error(`no test type '${key}' is declared for this tenant`);
  CHANGED.set(key, { ...CHANGED.get(key), ...change });
  return mockTestTypes().find((t) => t.key === key) as TestType;
}

export function mockTestTypes(): TestType[] {
  return baseTestTypes().map((t) => ({ ...t, ...CHANGED.get(t.key) }));
}

function baseTestTypes(): TestType[] {
  return [
    {
      key: "agl_photometric",
      name: "AGL photometric performance",
      description:
        "Light output, beam alignment and colour for airfield ground lighting, measured against the in-service minimum for the fitting.",
      frameworks: ["ICAO Annex 14", "GACA GACAR 139"],
      mode: "ratio",
      measurementKey: "intensity_pct",
      unit: "% of specified",
      intervalDays: 180,
      intervalStatus: "proposed",
      thresholds: [
        {
          label: "In-service intensity, share of specified",
          attention: 70,
          fail: 50,
          basis: "pct_of_specified",
          higherIsBetter: true,
          status: "proposed",
          basisNote:
            "Annex 14 treats a fitting below 70% of specified intensity as needing maintenance and below 50% as unserviceable. Proposed until the airport sets its own.",
        },
      ],
      ratio: {
        value: "measured_cd",
        valueLabel: "Measured intensity (cd)",
        limit: "specified_cd",
        limitLabel: "Specified intensity (cd)",
        status: "proposed",
      },
      summaryFields: [
        { key: "fittings", label: "Fittings measured", type: "number" },
        { key: "below_70", label: "Below 70% of specified", type: "number" },
        { key: "circuit", label: "Circuit", type: "text" },
      ],
    },
    {
      key: "insulation_resistance",
      name: "Insulation resistance, series circuit",
      description:
        "Insulation resistance of an airfield lighting series circuit at 5 kV, measured from the regulator with the circuit isolated.",
      frameworks: ["IEC 61557", "GACA GACAR 139"],
      mode: "threshold",
      measurementKey: "resistance_mohm",
      unit: "MΩ",
      intervalDays: 90,
      intervalStatus: "proposed",
      thresholds: [
        {
          label: "Circuit insulation resistance",
          attention: 50,
          fail: 10,
          basis: "value",
          higherIsBetter: true,
          status: "proposed",
          basisNote:
            "Commonly 50 MΩ as the point to investigate and 10 MΩ as the point to take the circuit out of service. Proposed.",
        },
      ],
      summaryFields: [
        { key: "circuit", label: "Circuit", type: "text" },
        { key: "regulator", label: "Regulator", type: "text" },
      ],
    },
    {
      key: "earth_continuity",
      name: "Earth continuity and bonding",
      description: "Continuity of the protective earth from the regulator to each pit and fitting.",
      frameworks: ["IEC 60364"],
      mode: "threshold",
      measurementKey: "resistance_ohm",
      unit: "Ω",
      intervalDays: 365,
      intervalStatus: "proposed",
      thresholds: [
        {
          label: "Earth path resistance",
          attention: 1,
          fail: 2,
          basis: "value",
          higherIsBetter: false,
          status: "proposed",
          basisNote: "A commonly used working limit, not a value this airport has agreed.",
        },
      ],
      summaryFields: [{ key: "points", label: "Points tested", type: "number" }],
    },
    {
      key: "runway_friction",
      name: "Runway friction survey",
      description: "Continuous friction measurement along each third of the runway, wet, at the surveyed speed.",
      frameworks: ["ICAO Annex 14", "ICAO Doc 9137"],
      mode: "threshold",
      measurementKey: "mu",
      unit: "µ",
      intervalDays: 120,
      intervalStatus: "proposed",
      thresholds: [
        {
          label: "Friction, maintenance planning level",
          attention: 0.53,
          fail: 0.43,
          basis: "value",
          higherIsBetter: true,
          status: "proposed",
          basisNote:
            "Annex 14 maintenance planning and minimum friction levels vary by device and speed; these are for the device named on the record. Proposed.",
        },
      ],
      summaryFields: [
        { key: "runway", label: "Runway", type: "text" },
        { key: "device", label: "Device", type: "text" },
        { key: "speed_kph", label: "Survey speed (km/h)", type: "number" },
      ],
    },
    {
      key: "pavement_core",
      name: "Pavement core and layer thickness",
      description: "Cores taken through the surface course, with layer thickness and condition recorded per core.",
      frameworks: ["ASTM D5361"],
      mode: "judged",
      intervalStatus: "proposed",
      thresholds: [],
      summaryFields: [
        { key: "cores", label: "Cores taken", type: "number" },
        { key: "area", label: "Area", type: "text" },
      ],
    },
    {
      key: "standby_generator",
      name: "Standby generator load test",
      description: "On-load test of a standby set, with changeover time and the load held for the test period.",
      frameworks: ["ISO 8528"],
      mode: "threshold",
      measurementKey: "changeover_s",
      unit: "s",
      intervalDays: 30,
      intervalStatus: "proposed",
      thresholds: [
        {
          label: "Changeover time",
          attention: 12,
          fail: 15,
          basis: "value",
          higherIsBetter: false,
          status: "proposed",
          basisNote: "Annex 14 sets maximum switch-over times by aid; 15 s is the common figure for a runway aid.",
        },
      ],
      summaryFields: [
        { key: "set", label: "Generator set", type: "text" },
        { key: "load_pct", label: "Load held (%)", type: "number" },
      ],
    },
    /* build 7: the assessments board's records, as migration 0094 declares the type */
    {
      key: "assessment",
      name: "Assessment (from the assessments board)",
      description:
        "An assessment, survey or statutory test filed on the assessments board, with the assessor's verdict and its basis. The kind of assessment is kept on the record.",
      frameworks: [],
      mode: "judged",
      unit: undefined,
      intervalStatus: "proposed",
      thresholds: [],
      summaryFields: [
        { key: "board_type", label: "Kind of assessment", type: "text" },
        { key: "result", label: "Result as filed", type: "text" },
      ],
    },
  ];
}

interface Seed {
  ref: string;
  type: string;
  title: string;
  daysAgo: number;
  by?: string;
  method?: string;
  device?: string;
  scope?: string;
  outcome: TestRecord["outcome"];
  basis: string;
  notes?: string;
  summary: Record<string, unknown>;
  results: number;
  dueInDays?: number;
}

const SEEDS: Seed[] = [
  {
    ref: "TR-2026-0041",
    type: "agl_photometric",
    title: "RWY 17/35 centreline, photometric sweep",
    daysAgo: 12,
    by: "Gulf Photometrics",
    method: "Mobile photometric trailer, two passes per direction",
    device: "ALMS-3 mobile photometer",
    scope: "RWY 17/35 centreline, 900 fittings",
    outcome: "attention",
    basis: "41 fittings below 70% of specified intensity; none below 50%.",
    notes:
      "The low readings cluster between CH 1200 and CH 1500, the same stretch the friction survey flagged. Engineering note: clean and re-measure before deciding to replace — the fittings are four years into a ten year life and the drop looks like contamination, not failure.",
    summary: { fittings: 900, below_70: 41, circuit: "CL-1" },
    results: 900,
    dueInDays: 168,
  },
  {
    ref: "TR-2026-0040",
    type: "insulation_resistance",
    title: "Series circuit TE-2 insulation resistance",
    daysAgo: 21,
    by: "Zenith Electrical Testing",
    method: "5 kV insulation test from the regulator, circuit isolated",
    device: "Megger MIT525",
    scope: "Circuit TE-2, taxiway edge",
    outcome: "fail",
    basis: "8.2 MΩ, below the 10 MΩ point at which the circuit is taken out of service.",
    notes:
      "Engineering note: water ingress at pit TE-2/14 is the likely cause — the pit was flooded at the last inspection. Circuit isolated and a work order raised. Do not re-energise on the strength of a dry day.",
    summary: { circuit: "TE-2", regulator: "CCR-4" },
    results: 1,
    dueInDays: 69,
  },
  {
    ref: "TR-2026-0039",
    type: "runway_friction",
    title: "RWY 17/35 friction survey, wet",
    daysAgo: 34,
    by: "Sand & Surface Consultants",
    method: "Continuous friction measurement, self-wetting, 65 km/h",
    device: "GripTester Mk2",
    scope: "RWY 17/35, three thirds, both directions",
    outcome: "attention",
    basis: "Touchdown third at 0.51 µ, below the maintenance planning level of 0.53.",
    notes:
      "Rubber build-up in the 17 touchdown zone. Engineering note: removal is already scheduled; re-survey within two weeks of the removal rather than waiting for the next interval.",
    summary: { runway: "17/35", device: "GripTester Mk2", speed_kph: 65 },
    results: 6,
    dueInDays: 86,
  },
  {
    ref: "TR-2026-0038",
    type: "standby_generator",
    title: "Generator set 2, on-load test",
    daysAgo: 8,
    by: "Zenith Electrical Testing Co.",
    method: "On-load test, 60 minutes at the available airfield load",
    device: "Permanent metering",
    scope: "Generator set 2, north substation",
    outcome: "pass",
    basis: "Changeover 9.4 s, inside the 15 s figure used for a runway aid.",
    notes: "Held 74% load for the hour. Fuel polished in the same visit.",
    summary: { set: "GEN-2", load_pct: 74 },
    results: 1,
    dueInDays: 22,
  },
  {
    ref: "TR-2026-0037",
    type: "earth_continuity",
    title: "Apron 1 lighting, earth continuity",
    daysAgo: 55,
    method: "Two-wire continuity, regulator to pit",
    scope: "Apron 1 flood masts and pits",
    outcome: "pass",
    basis: "Highest reading 0.7 Ω, inside the 1 Ω point to investigate.",
    notes:
      "Engineering note: no service provider was recorded against this test when it was entered. The readings are in the file; who took them is not. Treat the result as unverified until somebody can say.",
    summary: { points: 34 },
    results: 34,
    dueInDays: 310,
  },
  {
    ref: "TR-2026-0036",
    type: "pavement_core",
    title: "TWY B cores, layer thickness",
    daysAgo: 78,
    by: "Sand & Surface Consultants",
    method: "150 mm cores, layer thickness and condition per core",
    scope: "TWY B between C and D",
    outcome: "attention",
    basis: "Judged: surface course thinner than the design in two of six cores.",
    notes:
      "Engineering note: the two thin cores are both in the 2019 overlay, not the original construction. Worth pricing a strengthening design before the next pavement assessment rather than after it.",
    summary: { cores: 6, area: "TWY B, C–D" },
    results: 6,
  },
  {
    ref: "TR-2026-0035",
    type: "agl_photometric",
    title: "TWY C centreline, photometric sweep",
    daysAgo: 96,
    by: "Gulf Photometrics",
    method: "Mobile photometric trailer, one pass per direction",
    device: "ALMS-3 mobile photometer",
    scope: "TWY C centreline, 410 fittings",
    outcome: "pass",
    basis: "No fitting below 70% of specified intensity.",
    summary: { fittings: 410, below_70: 0, circuit: "TC-1" },
    results: 410,
    dueInDays: 84,
  },
  {
    ref: "TR-2026-0034",
    type: "insulation_resistance",
    title: "Series circuit CL-1 insulation resistance",
    daysAgo: 112,
    by: "Zenith Electrical Testing",
    method: "5 kV insulation test from the regulator, circuit isolated",
    device: "Megger MIT525",
    scope: "Circuit CL-1, runway centreline",
    outcome: "attention",
    basis: "46 MΩ, below the 50 MΩ point at which the circuit is investigated.",
    notes: "Falling steadily across the last three tests. Engineering note: trend it rather than treating each reading alone.",
    summary: { circuit: "CL-1", regulator: "CCR-1" },
    results: 1,
    dueInDays: -22,
  },
  {
    ref: "TR-2026-0033",
    type: "standby_generator",
    title: "Generator set 1, on-load test",
    daysAgo: 39,
    by: "Zenith Electrical Testing Co.",
    method: "On-load test, 60 minutes at the available airfield load",
    scope: "Generator set 1, south substation",
    outcome: "pass",
    basis: "Changeover 11.1 s, inside the 15 s figure used for a runway aid.",
    summary: { set: "GEN-1", load_pct: 68 },
    results: 1,
    dueInDays: -9,
  },
];

export function mockTestRecords(now = new Date()): TestRecord[] {
  const types = new Map(mockTestTypes().map((t) => [t.key, t]));
  return SEEDS.map((sd, i) => {
    const ty = types.get(sd.type)!;
    return {
      id: `tr-${String(i + 1).padStart(3, "0")}`,
      reference: sd.ref,
      testType: sd.type,
      testTypeName: ty.name,
      frameworks: ty.frameworks,
      title: sd.title,
      performedOn: ago(now, sd.daysAgo),
      performedBy: sd.by,
      method: sd.method,
      device: sd.device,
      scope: sd.scope,
      summary: sd.summary,
      outcome: sd.outcome,
      outcomeBasis: sd.basis,
      judged: ty.mode === "judged",
      counts: {},
      notes: sd.notes,
      nextDue: sd.dueInDays === undefined ? undefined : ahead(now, sd.dueInDays),
      demo: true,
      createdAt: new Date(now.getTime() - sd.daysAgo * DAY).toISOString(),
      createdBy: "Demonstration data",
      results: sd.results,
      files: 0,
      actions: 0,
      revision: 1,
      source: "demo",
    };
  });
}

/**
 * The summary tiles, computed from the register rather than stated.
 *
 * Deriving it here means the tiles cannot disagree with the rows underneath
 * them, which is the failure a hand-written summary always eventually has.
 */
export function mockTestingSummary(records: TestRecord[], now = new Date()): TestingSummary {
  const today = iso(now);
  const types = mockTestTypes();
  const latestOutcomes = { fail: 0, attention: 0, pass: 0, not_assessed: 0 };
  let overdue = 0;
  let dueSoon = 0;
  let neverTested = 0;

  /* as the platform: a type nothing makes mandatory, with no interval and no record, is not a gap (build 7) */
  const used = types.filter(
    (ty) => ty.intervalDays || ty.frameworks.length || records.some((r) => r.testType === ty.key),
  );
  const summaries = used.map((ty) => {
    const mine = records
      .filter((r) => r.testType === ty.key)
      .sort((a, b) => b.performedOn.localeCompare(a.performedOn));
    const last = mine[0] ?? null;
    if (!last) {
      neverTested += 1;
      return {
        testType: ty.key,
        name: ty.name,
        frameworks: ty.frameworks,
        mode: ty.mode,
        intervalDays: ty.intervalDays,
        intervalStatus: ty.intervalStatus,
        records: 0,
        last: null,
        state: "never_tested" as const,
      };
    }
    latestOutcomes[last.outcome] += 1;
    const due = last.nextDue;
    const state = !ty.intervalDays
      ? ("no_interval" as const)
      : !due
        ? ("no_interval" as const)
        : due < today
          ? ("overdue" as const)
          : Date.parse(due) - now.getTime() < 30 * DAY
            ? ("due_soon" as const)
            : ("current" as const);
    if (state === "overdue") overdue += 1;
    if (state === "due_soon") dueSoon += 1;
    return {
      testType: ty.key,
      name: ty.name,
      frameworks: ty.frameworks,
      mode: ty.mode,
      intervalDays: ty.intervalDays,
      intervalStatus: ty.intervalStatus,
      records: mine.length,
      last,
      nextDue: due,
      state,
    };
  });

  return { types: summaries, latestOutcomes, overdue, dueSoon, neverTested };
}

/** A record built from what somebody typed into the form. */
export function recordFromInput(
  input: NewTestInput,
  seq: number,
  now = new Date(),
): TestRecord {
  const ty = mockTestTypes().find((t) => t.key === input.testType);
  return {
    id: `tr-entered-${seq}`,
    reference: `TR-${now.getFullYear()}-${String(1000 + seq).slice(1)}`,
    testType: input.testType,
    testTypeName: ty?.name ?? input.testType,
    frameworks: ty?.frameworks ?? [],
    title: input.title?.trim() || (ty?.name ?? input.testType),
    performedOn: input.performedOn,
    performedBy: input.performedBy?.trim() || undefined,
    method: input.method,
    device: input.device,
    scope: input.scope,
    summary: input.summary ?? {},
    outcome: input.outcome ?? "not_assessed",
    outcomeBasis: input.outcomeBasis ?? "",
    judged: ty?.mode === "judged",
    counts: {},
    notes: input.notes,
    nextDue: input.nextDue || undefined,
    /* entered here, not demonstration data — the register keeps them apart */
    demo: false,
    createdAt: now.toISOString(),
    results: input.results?.length ?? 0,
    files: 0,
    actions: 0,
    revision: 1,
    source: input.source ?? "form",
    sourceFile: input.sourceFile,
  };
}
